import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {IMappingDefinitions, IRegoFeatureDefinition, IRegoMappingDefinition} from './mapping';

/**
 * Service for interacting with mappings.
 * Mapping data is basically list of linkable items from various source systems.
 */
@Injectable()
export class MappingService  {
  protected url = `${environment.regoApiURL}mappinglist`;

  private readonly cache: Map<string, Promise<any>> = new Map();

  /**
   * Default constructor
   * @param {HttpClient} http - refined http service, automatically adds authentication token
   */
  constructor(protected http: HttpClient) {
  }

  // this data changes rarely, and is cached on the server anyways, so no need to fetch it more than once after each page refresh
  private cachedGet<T>(url: string): Promise<T> {
    if (!this.cache.has(url)) {
      this.cache.set(url, this.http.get<T>(url).toPromise());
    }
    return this.cache.get(url);
  }

  public getMisoNetMappingList(): Promise<IMappingDefinitions> {
    return this.cachedGet<IMappingDefinitions>(`${this.url}/thirdparty/misonet`);
  }

  public getPksMappingList(): Promise<IMappingDefinitions> {
    return this.cachedGet<IMappingDefinitions>(`${this.url}/thirdparty/pks`);
  }

  public getValmetMappingList(): Promise<IMappingDefinitions> {
    return this.cachedGet<IMappingDefinitions>(`${this.url}/thirdparty/valmet`);
  }

  /**
   * 1) if single layerName is given fetch it
   * 2) if multiple layerNames are given fetch those
   * 3) if no layerNames are given fetch all layers
   * @param layers three options: one layername, array of layernames or no layername
   */
  public getRegoMappingList(layers?: string | string[]): Promise<IRegoMappingDefinition> {
    if (Array.isArray(layers)) {
      layers = layers.join(',');
    }
    return this.cachedGet<IRegoMappingDefinition>(`${this.url}/rego${layers ? `?layerNames=${layers}` : ''}`);
  }

  public async getRegoFeatureDefinition(id: string | number, layers?: string | string[]): Promise<IRegoFeatureDefinition | null> {
    const mappings = await this.getRegoMappingList(layers);
    for (const layer of mappings.layers) {
      for (const feature of layer.features) {
        if (`${feature.id}` === `${id}`) {
          return feature;
        }
      }
    }
    return null;
  }
}
